// The inputs behind a victory entry, read back from its `detail` text (world/victory.ts writes
// "label 12.3% (2024)" or "label 8.1% (2021–2024)" per input, joined by " · "), so a row can say
// WHICH input is old instead of greying the whole row for the oldest one.
//
//   tourist arrivals 3.2% (2005) · World Heritage 12/1200 · Olympic medals 8.1% (2021–2024)
//   └ old: chip "incl. 2005" (the tip names it)        └ no year: current       └ 2024

import type { DateStr } from '../core/schema.ts';
import type { VictoryEntry } from '../world/api.ts';
import { isStale } from './format.ts';

const PART_SEP = ' · ';
const NOT_SCORED = 'not scored';
const NO_DATA = ': no data';
const YEARS_SHOWN = 2;

// "head (2005)" or "head (2021–2024)": the year of a range is its newest, as the world counts it.
const YEAR_TAIL = /^(.*?)\s*\((\d{4})(?:[–-](\d{4}))?\)$/;
// The value token closing a head ("12.3%", "5/10", "42.1"): a token with a digit.
const VALUE_TAIL = /\s+\S*\d\S*$/;

export interface Part {
  text: string;
  label: string;
  /** Newest data year of the input; null when the text gives none (current as of the date). */
  year: number | null;
  /** False for an input with no value or one left out of the score: it has no age. */
  counted: boolean;
}

function partOf(text: string): Part {
  const counted = !text.startsWith(NOT_SCORED) && !text.endsWith(NO_DATA);
  const m = YEAR_TAIL.exec(text);
  if (!m) {
    return { text, label: text, year: null, counted };
  }
  return { text, label: m[1].replace(VALUE_TAIL, ''), year: Number(m[3] ?? m[2]), counted };
}

export function partsOf(detail: string): Part[] {
  return detail === '' ? [] : detail.split(PART_SEP).map(partOf);
}

export interface RowAge {
  /** Every input is old (or, with no input years to read, the entry's own year is): grey the row. */
  grey: boolean;
  /** Old inputs of a row that is otherwise current, oldest first. */
  stale: Part[];
}

/** The old inputs, oldest first. */
function oldest(parts: Part[]): Part[] {
  return [...parts].sort((a, b) => (a.year ?? 0) - (b.year ?? 0));
}

export function ageOf(entry: Pick<VictoryEntry, 'detail' | 'dataYear'>, at: DateStr): RowAge {
  const counted = partsOf(entry.detail).filter((p) => p.counted);
  const dated = counted.filter((p) => p.year !== null);
  if (dated.length === 0) {
    return { grey: isStale(entry.dataYear, at), stale: [] };
  }

  const stale = dated.filter((p) => isStale(p.year as number, at));
  if (stale.length === counted.length) {
    return { grey: true, stale: [] };
  }
  return { grey: false, stale: oldest(stale) };
}

/** 'incl. 2005 tourist arrivals': the chip for a current row with old inputs. */
export function inclText(stale: Part[]): string {
  return `incl. ${stale.map((p) => `${p.year} ${p.label}`).join(', ')}`;
}

/** 'incl. 2005': the short chip of a bar row (the row has no room for the input's name; the tip has). */
export function inclYears(stale: Part[]): string {
  const years = [...new Set(stale.map((p) => p.year))];
  const more = years.length > YEARS_SHOWN ? ` +${years.length - YEARS_SHOWN}` : '';
  return `incl. ${years.slice(0, YEARS_SHOWN).join(', ')}${more}`;
}
